/**
 * product-quick-add custom element
 * 悬浮加购栏：使用 IntersectionObserver 监听 product-form-component 和 footer，
 * 结合 scroll 事件控制显示/隐藏。
 *
 * 逻辑：
 * - 将页面分为三部分：product-form-component 之前、之间、footer 之后
 * - H5端：用户未滚动到 product-form-component 时（页面顶部），滚动超过200px后显示
 * - PC端 + H5端：用户滚动过 product-form-component（底部不可见）且未到达 footer 时显示
 * - 到达 footer 后隐藏
 *
 * 加购逻辑：
 * - 点击按钮 -> 读取产品表单中的 variant id -> 调用 cart/add.js API
 * - 加购期间按钮显示 loading spinner，完成后恢复文本
 * - 成功后 dispatch cart:update 事件以更新购物车计数
 */
class ProductQuickAdd extends HTMLElement {
  #scopeFromPassed = false;
  #buyButtonsFormNotReach = false;
  #scopeToReached = false;
  #ticking = false;
  #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));
  #isAdding = false;

  connectedCallback() {
    const sectionEl = document.querySelector('.product-information--main')?.closest('.shopify-section');

    // scopeFrom: .product-information__details 内的 product-form-component
    this._scopeFrom = sectionEl
      ? sectionEl.querySelector('.product-information__details product-form-component')
      : document.querySelector('.product-information__details product-form-component');

    // scopeTo: footer
    this._scopeTo = document.querySelector('footer.footer');

    if (!this._scopeFrom || !this._scopeTo) {
      return;
    }

    this.#intersectionObserver.observe(this._scopeFrom);
    this.#intersectionObserver.observe(this._scopeTo);

    this._onScrollHandler = this._onScroll.bind(this);
    window.addEventListener('scroll', this._onScrollHandler, { passive: true });

    // 绑定所有加购按钮的点击事件
    this._buttons = this.querySelectorAll('button[data-form-id]');
    this._onClickHandler = this._handleAddToCart.bind(this);
    this._buttons.forEach((btn) => {
      btn.addEventListener('click', this._onClickHandler);
    });

    // 监听变体切换事件，更新悬浮栏价格/划线价/OFF值/按钮状态
    this._onVariantUpdateHandler = this._onVariantUpdate.bind(this);
    document.addEventListener('variant:update', this._onVariantUpdateHandler);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    if (this._onScrollHandler) {
      window.removeEventListener('scroll', this._onScrollHandler);
    }
    if (this._buttons && this._onClickHandler) {
      this._buttons.forEach((btn) => {
        btn.removeEventListener('click', this._onClickHandler);
      });
    }
    if (this._onVariantUpdateHandler) {
      document.removeEventListener('variant:update', this._onVariantUpdateHandler);
    }
  }

  // ==================== 显示/隐藏逻辑 ====================

  _onObserved(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._scopeFrom) {
        const rect = entry.boundingClientRect;
        this.#scopeFromPassed = rect.bottom < 0;
        this.#buyButtonsFormNotReach = rect.top > window.innerHeight;
      }
      if (entry.target === this._scopeTo) {
        this.#scopeToReached = entry.isIntersecting;
      }
    });
    this._updateVisibility();
  }

  _onScroll() {
    if (!this.#ticking) {
      window.requestAnimationFrame(() => {
        this._updateVisibility();
        this.#ticking = false;
      });
      this.#ticking = true;
    }
  }

  _updateVisibility() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const mobileTopShow = isMobile && this.#buyButtonsFormNotReach;/* 这里之前有个200的判断： && window.pageYOffset >= 200 */
    const normalShow = this.#scopeFromPassed && !this.#scopeToReached;
    const toVisible = mobileTopShow || normalShow;
    this.classList.toggle('is-visible', toVisible);
  }

  // ==================== 加购逻辑 ====================

  // ==================== 变体切换 → 更新悬浮栏 ====================

  /**
   * 格式化金额（分 → 货币字符串）
   */
  _formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // 简单 fallback：使用 FoxTheme.moneyFormat 或默认 ${{amount}}
    const format = window.FoxTheme?.moneyFormat || window.Shopify?.currency?.money_format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100);
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimalsWithComma = String(amountNoDecimals).replace('.', ',');
    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  /**
   * 监听 variant:update 事件，更新悬浮栏的价格、划线价、OFF值、按钮状态
   */
  _onVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    // 确保事件来自同一个 section
    const sectionId = this.dataset.sectionId;
    const eventSectionId = event.detail?.sourceId;
    // sourceId 可能是 optionValueId，也可能是 sectionId，通过检查 productId 关联
    // 更可靠的方式：检查事件来源是否在同一 shopify-section 下
    const sectionEl = this.closest('.shopify-section') || document.getElementById(`shopify-section-${sectionId}`);
    if (sectionEl && !sectionEl.contains(event.target)) return;

    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;
    const available = variant.available;
    const hasDiscount = compareAtPrice && compareAtPrice > price;

    // --- H5端 ---
    const mobilePrice = this.querySelector('[data-quick-add-price]');
    const mobileSavings = this.querySelector('[data-quick-add-savings]');
    const mobileCompare = this.querySelector('[data-quick-add-compare-price]');

    if (mobilePrice) {
      mobilePrice.textContent = this._formatMoney(price);
    }

    if (hasDiscount) {
      const savingsAmount = Math.floor((compareAtPrice - price) / 100);
      if (mobileSavings) {
        mobileSavings.textContent = `$${savingsAmount} OFF`;
        mobileSavings.style.display = '';
      }
      if (mobileCompare) {
        mobileCompare.textContent = this._formatMoney(compareAtPrice);
        mobileCompare.style.display = '';
      }
    } else {
      if (mobileSavings) mobileSavings.style.display = 'none';
      if (mobileCompare) mobileCompare.style.display = 'none';
    }

    // --- PC端 ---
    const desktopPrice = this.querySelector('[data-quick-add-price-desktop]');
    const desktopCompare = this.querySelector('[data-quick-add-compare-price-desktop]');

    if (desktopPrice) {
      desktopPrice.textContent = this._formatMoney(price);
    }

    if (hasDiscount) {
      if (desktopCompare) {
        desktopCompare.textContent = this._formatMoney(compareAtPrice);
        desktopCompare.style.display = '';
      }
    } else {
      if (desktopCompare) desktopCompare.style.display = 'none';
    }

    // --- 按钮状态 ---
    this._buttons.forEach((btn) => {
      btn.disabled = !available;
    });
  }

  /**
   * 从产品表单中获取当前选中的 variant ID
   */
  _getVariantId(formId) {
    const form = document.getElementById(formId);
    if (!form) return null;
    const input = form.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  /**
   * 获取当前数量（从表单的 quantity input 中读取，默认为 1）
   */
  _getQuantity(formId) {
    const form = document.getElementById(formId);
    if (!form) return 1;
    const input = form.querySelector('input[name="quantity"]');
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  /**
   * 设置按钮的 loading 状态
   */
  _setButtonLoading(button, loading) {
    if (loading) {
      button.classList.add('btn--loading');
      button.disabled = true;
      const spinner = button.querySelector('.loading-spinner');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.classList.remove('btn--loading');
      button.disabled = false;
      const spinner = button.querySelector('.loading-spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /**
   * 同步设置所有悬浮加购按钮的 loading 状态
   */
  _setAllButtonsLoading(loading) {
    this._buttons.forEach((btn) => this._setButtonLoading(btn, loading));
  }

  /**
   * 点击加购按钮的处理函数
   */
  async _handleAddToCart(event) {
    const button = event.currentTarget;
    if (this.#isAdding || button.disabled) return;

    const formId = button.dataset.formId;
    if (!formId) return;

    const variantId = this._getVariantId(formId);
    if (!variantId) return;

    const quantity = this._getQuantity(formId);

    this.#isAdding = true;
    this._setAllButtonsLoading(true);

    try {
      const response = await fetch(
        (window.FoxTheme?.routes?.cart_add_url) || (window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: quantity }],
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Add to cart error:', data.description || data.message);
      } else {
        // 派发 cart:update 事件，让购物车计数等组件自动更新
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: formId,
              data: {
                isIncremental: true,
                itemCount: quantity,
                source: 'product-quick-add',
              },
            },
          })
        );
      }
    } catch (error) {
      console.error('Add to cart fetch error:', error);
    } finally {
      this.#isAdding = false;
      this._setAllButtonsLoading(false);
    }
  }
}

if (!window.customElements.get('product-quick-add')) {
  window.customElements.define('product-quick-add', ProductQuickAdd);
}
